import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, Menu as MenuIcon } from "lucide-react";
import { useCart } from "@/context/CartContext";
import StoreStatusBadge from "@/components/store/StoreStatusBadge";
import { useAuth } from "@/context/AuthContext";
import MobileMenu from "@/components/MobileMenu";
import { scrollToMenuSection } from "@/components/MobileCartBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Header({ onAuthClick }) {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToMenu = () => scrollToMenuSection(navigate);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 w-full z-50 px-5 sm:px-8 py-4 flex items-center justify-between transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-biga-cream/85 border-b border-biga-ink/10" : "bg-transparent"
      }`}
    >
      <div className="flex items-center gap-3 shrink-0">
      <Link to="/" data-testid="logo-link" className="block h-12 sm:h-14 hover:scale-105 hover:rotate-[-3deg] transition-transform duration-300" aria-label="BIGA Artisanal Pizzeria home">
        <img src={`${BACKEND}/api/assets/biga-orange-logo`} alt="BIGA Artisanal Pizzeria" height={56} className="h-full w-auto" />
      </Link>
      <StoreStatusBadge />
      </div>

      <nav className="hidden md:flex items-center gap-4 xl:gap-8 ml-4 font-semibold text-sm whitespace-nowrap" data-testid="desktop-nav">
        <button onClick={scrollToMenu} data-testid="nav-menu" className="hover:text-biga-orange transition-colors">Menu</button>
        <Link to="/nutrition" data-testid="nav-nutrition" className="hidden lg:inline hover:text-biga-orange transition-colors">Nutrition</Link>
        <Link to="/gallery" data-testid="nav-gallery" className="hidden lg:inline hover:text-biga-orange transition-colors">Gallery</Link>
        <Link to="/about" data-testid="nav-about" className="hidden lg:inline hover:text-biga-orange transition-colors">About</Link>
        {user && <Link to="/orders" data-testid="nav-orders" className="hover:text-biga-orange transition-colors whitespace-nowrap">My Orders</Link>}
        {user && <Link to="/account" data-testid="nav-account" className="hover:text-biga-orange transition-colors">Account</Link>}
        {user?.role === "admin" && <Link to="/admin" data-testid="nav-admin" className="hover:text-biga-orange transition-colors font-bold">Dashboard</Link>}
        {user?.role === "accountant" && <Link to="/admin/accounting" data-testid="nav-books" className="hover:text-biga-orange transition-colors font-bold">Books</Link>}
      </nav>

      <div className="flex items-center gap-2 sm:gap-3">
        {user ? (
          <>
            <Link
              to="/account"
              data-testid="mobile-account-btn"
              aria-label="My account"
              className="md:hidden p-2 rounded-full hover:bg-biga-ink/5"
            >
              <User size={20} />
            </Link>
            <div className="hidden md:flex items-center gap-2">
              <span className="hidden xl:inline text-sm font-semibold whitespace-nowrap" data-testid="header-greeting">Ciao, {user.name.split(" ")[0]}</span>
              <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-full hover:bg-biga-ink/5" aria-label="Log out">
                <LogOut size={18} />
              </button>
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onAuthClick}
              data-testid="mobile-auth-btn"
              aria-label="Sign in"
              className="sm:hidden p-2 rounded-full hover:bg-biga-ink/5"
            >
              <User size={20} />
            </button>
            <button
              type="button"
              onClick={onAuthClick}
              data-testid="header-auth-btn"
              className="hidden sm:flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-full border-2 border-biga-ink hover:bg-biga-ink hover:text-biga-cream transition-colors"
            >
              <User size={16} /> Sign in
            </button>
          </>
        )}

        <button
          onClick={() => setOpen(true)}
          data-testid="cart-toggle"
          aria-label="Open cart"
          className="relative flex items-center gap-2 bg-biga-orange text-biga-cream font-bold text-sm px-4 py-2.5 rounded-full hover:bg-biga-green transition-colors"
        >
          <ShoppingBag size={18} />
          <span className="hidden sm:inline md:hidden xl:inline">Cart</span>
          {count > 0 && (
            <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-biga-green text-biga-cream text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-biga-cream">
              {count}
            </span>
          )}
        </button>
        <button type="button" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu" aria-expanded={menuOpen}
          className="lg:hidden p-2 rounded-full hover:bg-biga-ink/5">
          <MenuIcon size={22} />
        </button>
        <MobileMenu open={menuOpen} onOpenChange={setMenuOpen} onMenu={scrollToMenu} onAuthClick={onAuthClick} />
      </div>
    </header>
  );
}
